import { TsDOM as q } from '@/scripts/core/TsDOM'
import { type PredictiveSearch } from '@/scripts/optional/predictive-search'
import { type DetailsModal } from '@/scripts/theme/details-modal'
import { type HeaderMenu } from '@/scripts/theme/header-menu'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { SELECTORS } from '@/scripts/core/global'

let mobileMenuScrollTop: number | undefined
let accountTriggerObserver: MutationObserver | undefined

export function lockHeaderDrawerScroll() {
	let mobileViewport = window.innerWidth < 990
	document.addEventListener(
		'click',
		(event) => {
			const target = event.target
			if (!(target instanceof Element) || window.innerWidth >= 990) return
			const summary = target.closest('header-drawer > details > summary')
			if (!summary || summary.parentElement?.hasAttribute('open')) return
			mobileMenuScrollTop = window.scrollY
		},
		{ capture: true }
	)
	window.addEventListener(
		'scroll',
		() => {
			if (
				window.innerWidth >= 990 ||
				mobileMenuScrollTop === undefined ||
				!document.querySelector('header-drawer > details[open]')
			) {
				mobileMenuScrollTop = undefined
				return
			}
			if (window.scrollY !== mobileMenuScrollTop)
				window.scrollTo(0, mobileMenuScrollTop)
		},
		{ passive: true }
	)
	window.addEventListener('resize', () => {
		const isMobile = window.innerWidth < 990
		if (!isMobile) mobileMenuScrollTop = undefined
		else if (
			!mobileViewport &&
			document.querySelector('header-drawer > details[open]')
		)
			mobileMenuScrollTop = window.scrollY
		mobileViewport = isMobile
	})
}

export function syncAccountTriggerPart() {
	document.querySelectorAll('shopify-account').forEach((account) => {
		const button = account.shadowRoot?.querySelector('.account-button')
		if (button instanceof HTMLElement && !button.hasAttribute('part')) {
			button.setAttribute('part', 'signed-out-avatar')
		}
	})
}

export function startAccountTriggerPartSync() {
	const observe = () => {
		accountTriggerObserver?.disconnect()
		const account = document.querySelector('shopify-account')
		if (!account?.shadowRoot) return
		accountTriggerObserver = new MutationObserver(syncAccountTriggerPart)
		accountTriggerObserver.observe(account.shadowRoot, {
			childList: true,
			subtree: true,
		})
	}
	const boot = () => {
		syncAccountTriggerPart()
		observe()
	}
	boot()
	document.addEventListener('shopify-render', syncAccountTriggerPart)
	document.addEventListener('shopify:section:load', boot)
}

export class StickyHeader extends UcoastEl {
	static htmlSelector = 'sticky-header'
	preventHide: boolean = false
	preventReveal: boolean = false
	header: HTMLElement
	headerIsAlwaysSticky?: boolean
	headerBounds: DOMRect | {} = {}
	currentScrollTop: number = 0
	predictiveSearch?: PredictiveSearch
	onScrollHandler!: (event: Event) => void
	hideHeaderOnScrollUp!: () => void
	searchModal?: DetailsModal
	isScrolling?: number
	topTransitionTimeout?: number
	scrollDirectionAnchor: number = 0
	disclosures?: [] | NodeListOf<HeaderMenu>
	setHeaderHeightHandler?: () => void
	headerResizeObserver?: ResizeObserver
	announcement?: HTMLElement
	accountComponent?: HTMLElement
	backdrop?: HTMLElement
	usesScrollBackdrop: boolean = false
	setAccountDialogPositionHandler?: () => void
	constructor() {
		super()
		this.header = q.rs(SELECTORS.sectionHeader)
	}

	override connectedCallback() {
		this.header = q.rs(SELECTORS.sectionHeader)
		this.backdrop = q.os<HTMLElement>('[data-header-scroll-backdrop]')
		this.usesScrollBackdrop =
			this.getAttribute('data-header-background-type') === 'blur'
		this.setBackdropState('top')
		this.headerIsAlwaysSticky =
			this.getAttribute('data-sticky-type') === 'always' ||
			this.getAttribute('data-sticky-type') === 'reduce-logo-size'
		this.headerBounds = {}

		this.setHeaderHeight()

		const setHeaderHeightHandler = this.setHeaderHeight.bind(this)
		this.setHeaderHeightHandler = setHeaderHeightHandler
		document.addEventListener('shopify:section:load', setHeaderHeightHandler)
		document.addEventListener('shopify:section:unload', setHeaderHeightHandler)

		this.headerResizeObserver = new ResizeObserver(() => {
			this.setHeaderHeight()
		})
		this.headerResizeObserver.observe(this.header)
		this.setHeaderHeight()
		this.accountComponent = q.os<HTMLElement>('shopify-account', this)
		const setAccountDialogPositionHandler =
			this.setAccountDialogPosition.bind(this)
		this.setAccountDialogPositionHandler = setAccountDialogPositionHandler
		this.accountComponent?.addEventListener(
			'click',
			setAccountDialogPositionHandler,
			{ capture: true }
		)

		if (this.headerIsAlwaysSticky && this.header) {
			this.header.classList.add('shopify-section-header-sticky')
		}

		this.currentScrollTop = 0
		this.scrollDirectionAnchor = 0
		this.preventReveal = false
		this.predictiveSearch = q.os<PredictiveSearch>(
			'predictive-search',
			this
		)

		this.onScrollHandler = this.onScroll.bind(this)
		this.hideHeaderOnScrollUp = () => (this.preventReveal = true)

		this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp)
		window.addEventListener('scroll', this.onScrollHandler, false)

		this.createObserver()
	}

	setHeaderHeight() {
		if (!this.header) throw new Error('no header element found')
		const headerHeight = `${this.header.getBoundingClientRect().height}px`
		document.documentElement.style.setProperty(
			'--header-height',
			headerHeight
		)
		if (this.hasAttribute('data-transparent-overlap')) {
			document.documentElement.style.setProperty(
				'--header-overlap-active',
				headerHeight
			)
		}
		const announcement = document.querySelector<HTMLElement>('.announcement-bar-section')
		if (announcement !== this.announcement) {
			if (this.announcement) this.headerResizeObserver?.unobserve(this.announcement)
			this.announcement = announcement ?? undefined
		}
		if (this.announcement) this.headerResizeObserver?.observe(this.announcement)
		document.documentElement.style.setProperty(
			'--announcement-height',
			`${this.announcement?.getBoundingClientRect().height ?? 0}px`
		)
		this.setHeaderVisibility(
			!this.header.classList.contains('shopify-section-header-hidden')
		)
		document.documentElement.setAttribute('data-header-measured', '')
	}

	setHeaderVisibility(isVisible: boolean) {
		document.documentElement.style.setProperty(
			'--header-visible-height',
			isVisible ? `${this.header.getBoundingClientRect().height}px` : '0px'
		)
		document.documentElement.toggleAttribute(
			'data-header-hidden',
			!isVisible
		)
	}

	override disconnectedCallback() {
		if (this.setHeaderHeightHandler) {
			document.removeEventListener('shopify:section:load', this.setHeaderHeightHandler)
			document.removeEventListener('shopify:section:unload', this.setHeaderHeightHandler)
		}
		this.headerResizeObserver?.disconnect()
		if (this.accountComponent && this.setAccountDialogPositionHandler) {
			this.accountComponent.removeEventListener(
				'click',
				this.setAccountDialogPositionHandler,
				{ capture: true }
			)
		}
		this.removeEventListener(
			'preventHeaderReveal',
			this.hideHeaderOnScrollUp
		)
		window.removeEventListener('scroll', this.onScrollHandler)
		window.clearTimeout(this.topTransitionTimeout)
		document.documentElement.style.removeProperty('--header-visible-height')
		document.documentElement.removeAttribute('data-header-hidden')
	}

	setAccountDialogPosition() {
		if (!this.accountComponent) return

		this.accountComponent.style.setProperty(
			'--shopify-account-dialog-position-top',
			`${Math.max(0, this.getBoundingClientRect().bottom)}px`
		)
	}

	createObserver() {
		this.headerBounds = this.header.getBoundingClientRect()

		let observer = new IntersectionObserver((entries, observer) => {
			this.headerBounds = entries[0].intersectionRect
			observer.disconnect()
		})

		observer.observe(this.header)
	}

	onScroll() {
		const scrollTop =
			window.pageYOffset || document.documentElement.scrollTop

		if (this.predictiveSearch && this.predictiveSearch.isOpen) return
		if (this.headerIsAlwaysSticky) {
			if (this.headerBounds instanceof DOMRectReadOnly) {
				this.setBackdropState(
					scrollTop > this.headerBounds.bottom ? 'visible' : 'top'
				)
			}
			this.currentScrollTop = scrollTop
			return
		}

		if (
			this.headerBounds instanceof DOMRectReadOnly &&
			scrollTop > this.currentScrollTop &&
			scrollTop > this.headerBounds.bottom
		) {
			this.scrollDirectionAnchor = scrollTop
			this.cancelTopTransition()
			this.header.classList.add('scrolled-past-header')
			if (!this.preventHide) {
				requestAnimationFrame(() => {
					if (!this.preventHide) this.hide()
				})
			}
		} else if (
			this.headerBounds instanceof DOMRectReadOnly &&
			scrollTop < this.currentScrollTop &&
			scrollTop > this.headerBounds.bottom
		) {
			this.cancelTopTransition()
			this.header.classList.add('scrolled-past-header')
			if (
				!this.preventReveal &&
				this.scrollDirectionAnchor - scrollTop >= 10
			) {
				requestAnimationFrame(() => {
					if (!this.preventReveal) this.reveal()
				})
			} else {
				window.clearTimeout(this.isScrolling)

				this.isScrolling = setTimeout(() => {
					this.preventReveal = false
				}, 66)

				if (!this.preventHide) {
					requestAnimationFrame(() => {
						if (!this.preventHide) this.hide()
					})
				}
			}
		} else if (
			this.headerBounds instanceof DOMRectReadOnly &&
			scrollTop <= Math.ceil(this.headerBounds.top)
		) {
			this.scrollDirectionAnchor = scrollTop
			requestAnimationFrame(this.reset.bind(this))
		} else if (!(this.headerBounds instanceof DOMRectReadOnly)) {
			console.warn(
				'onScroll used in sticky-header but headerBounds is not initialized'
			)
			console.log('headerBounds', this.headerBounds)
		}

		this.currentScrollTop = scrollTop
	}

	hide() {
		if (this.headerIsAlwaysSticky) return
		const shouldAnimate =
			this.header.classList.contains('shopify-section-header-sticky') &&
			!this.header.classList.contains('shopify-section-header-hidden')
		this.cancelTopTransition()
		this.header.classList.add(
			'shopify-section-header-hidden',
			'shopify-section-header-sticky'
		)
		this.header.classList.toggle('animate', shouldAnimate)
		this.setHeaderVisibility(false)
		this.setBackdropState('hidden')
		this.closeMenuDisclosure()
		this.closeSearchModal()
	}

	reveal() {
		if (this.headerIsAlwaysSticky) return
		this.cancelTopTransition()
		this.header.classList.add('shopify-section-header-sticky', 'animate')
		this.header.classList.remove('shopify-section-header-hidden')
		this.setHeaderVisibility(true)
		this.setBackdropState('visible')
	}

	reset() {
		if (this.headerIsAlwaysSticky) return
		if (this.header.classList.contains('header-returning-to-top')) return

		const wasScrolled = this.header.classList.contains(
			'scrolled-past-header'
		)
		this.header.classList.remove(
			'shopify-section-header-hidden',
			'shopify-section-header-sticky',
			'animate'
		)
		this.setHeaderVisibility(true)
		this.setBackdropState('top')

		if (!wasScrolled) return

		this.header.classList.add('header-returning-to-top')
		this.header.classList.remove('scrolled-past-header')

		const finishTransition = () => {
			this.header.classList.remove('header-returning-to-top')
			this.topTransitionTimeout = undefined
		}
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			finishTransition()
			return
		}

		this.topTransitionTimeout = window.setTimeout(finishTransition, 220)
	}

	cancelTopTransition() {
		window.clearTimeout(this.topTransitionTimeout)
		this.topTransitionTimeout = undefined
		this.header.classList.remove('header-returning-to-top')
	}

	setBackdropState(state: 'top' | 'hidden' | 'visible') {
		if (!this.backdrop) return
		this.backdrop.toggleAttribute(
			'data-visible',
			this.usesScrollBackdrop && state === 'visible'
		)
		this.backdrop.toggleAttribute(
			'data-hidden',
			this.usesScrollBackdrop && state === 'hidden'
		)
	}

	closeMenuDisclosure() {
		this.disclosures =
			this.disclosures ||
			q.ol<HeaderMenu>('header-menu', this.header)
		this.disclosures?.forEach((disclosure) => {
			disclosure.close()
		})
	}

	closeSearchModal() {
		this.searchModal =
			this.searchModal ??
			q.os<DetailsModal>('details-modal', this.header)
		this.searchModal?.close(false)
	}
}
